import {
    getFacadeContentQuery,
    GetFacadeContentQuery,
    getPageAndGlobalProps,
    RedirectPageParams,
    NotFoundParams,
    isPageNotFound,
    isRedirect,
    HotelPageQuery,
    GlobalContent,
    PageRedirects,
    isRerouteGlobal,
    preserveQueryParams,
    GlobalQuery,
} from '@honeycomb/data';
import { CmsContentPage, MinimalLayout } from '@honeycomb/ui';
import { GetServerSidePropsContext } from 'next';
import { Logo } from '../../components/Logo';
import { Masthead } from '../../components/Masthead';

export default function Landing(data: GetFacadeContentQuery) {
    const { getPage } = data;
    return <CmsContentPage {...{ getPage }} />;
}

Landing.getLayout = (page: React.ReactElement, props: Record<string, unknown>) => (
    <MinimalLayout logo={<Logo />} masthead={<Masthead showAccountMenuOnly={true} reduced={true} />} {...props}>
        {page}
    </MinimalLayout>
);

type LandingPageParams = {
    idString: string;
    slug: string[];
};

type LandingPageContext = GetServerSidePropsContext<LandingPageParams>;

type FacadeContentQueryParams = {
    props: GetFacadeContentQuery | HotelPageQuery;
};

export async function getServerSideProps(
    context: LandingPageContext
): Promise<FacadeContentQueryParams | RedirectPageParams | NotFoundParams> {
    context.res.setHeader('Cache-Control', 'public, max-age=600, stale-while-revalidate=3599, stale-while-error=3599');
    const pagePath = (context.resolvedUrl || '/').split('?')[0];
    const params = pagePath
        .split('?')[0] // strip query
        .split('/') // split path
        .filter(Boolean); // remove empty segments

    const data: { props: GlobalQuery & (HotelPageQuery | GetFacadeContentQuery) } = await getPageAndGlobalProps<
        GetFacadeContentQuery,
        LandingPageContext
    >(context, getFacadeContentQuery(params[0], params.slice(1), context.query?.isPreview));

    const globalContents = 'globalContents' in data.props ? (data.props.globalContents as GlobalContent[]) : [];
    const pageRedirects: PageRedirects = globalContents?.find((item) => item?.key === 'REDIRECTS')
        ?.content as PageRedirects;

    // Helper function to add query parameters to redirect responses
    const addQueryToRedirect = (
        redirectResult: RedirectPageParams | false
    ): RedirectPageParams | FacadeContentQueryParams => {
        if (!redirectResult || !context.query || Object.keys(context.query).length === 0) {
            return redirectResult || data;
        }
        const { destination } = redirectResult.redirect;
        return {
            redirect: {
                ...redirectResult.redirect,
                destination: preserveQueryParams(destination, context.query),
            },
        };
    };

    // Apply redirects in order, preserving query parameters if a redirect occurs
    const rerouteResult = isRerouteGlobal(pagePath, pageRedirects);
    const rerouteWithQuery = addQueryToRedirect(rerouteResult);
    if ('redirect' in rerouteWithQuery) return rerouteWithQuery;

    const notFoundResult = isPageNotFound(data.props as GetFacadeContentQuery);
    if (notFoundResult) return notFoundResult;

    const redirectResult = isRedirect(data.props as GetFacadeContentQuery);
    const redirectWithQuery = addQueryToRedirect(redirectResult);
    if ('redirect' in redirectWithQuery) return redirectWithQuery;

    return data;
}
